import React, { useState } from "react";
import {
  Send,
  Phone,
  Mail,
  MapPin,
  Globe,
  Monitor,
  Briefcase,
  CheckCircle,
} from "lucide-react";

const Contact = ({ isDarkMode }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Create mailto link with all form data
    const mailtoLink = `mailto:maarijwaseem7@gmail.com?subject=${encodeURIComponent(
      formData.subject || "Portfolio Contact"
    )}&body=${encodeURIComponent(
      `Name: ${formData.name}
      Email: ${formData.email}
      Subject: ${formData.subject}
      
      Message:
      ${formData.message}
      
      ---
      Sent from Portfolio Contact Form`
    )}`;

    // Open email client
    window.location.href = mailtoLink;

    // Show success message and reset form
    setShowSuccess(true);
    setFormData({ name: "", email: "", subject: "", message: "" });

    // Hide success message after 3 seconds
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <section
      id="contact"
      className={`py-10 transition-all duration-500 ${
        isDarkMode ? "bg-gray-800" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2
            className={`text-5xl md:text-6xl font-bold mb-6 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Get In Touch
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-6"></div>
          <p
            className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Let's work together to bring your ideas to life
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div>
              <h3
                className={`text-3xl font-bold mb-6 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Let's Build Together
              </h3>
            </div>

            {[
              {
                icon: <Globe className="w-8 h-8" />,
                title: "Web Applications",
                description: "Modern, responsive websites and web apps",
                gradient: "from-teal-500 to-cyan-500",
              },
              {
                icon: <Monitor className="w-8 h-8" />,
                title: "Desktop Application",
                description: "Powerful cross-platform desktop solutions",
                gradient: "from-green-500 to-emerald-500",
              },
              {
                icon: <Briefcase className="w-8 h-8" />,
                title: "Business Solutions",
                description: "Custom tools to streamline your operations",
                gradient: "from-indigo-500 to-purple-500",
              },
            ].map((project, index) => (
              <div key={index} className="flex items-start gap-6">
                <div
                  className={`bg-gradient-to-r ${project.gradient} p-4 rounded-xl shadow-lg flex-shrink-0`}
                >
                  <div className="text-white">{project.icon}</div>
                </div>
                <div>
                  <h4
                    className={`text-xl font-bold mb-2 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {project.title}
                  </h4>
                  <p
                    className={`text-lg ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {project.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {[
                {
                  label: "Name",
                  type: "text",
                  placeholder: "Your name",
                  name: "name",
                },
                {
                  label: "Email",
                  type: "email",
                  placeholder: "your.email@example.com",
                  name: "email",
                },
                {
                  label: "Subject",
                  type: "text",
                  placeholder: "Project discussion",
                  name: "subject",
                },
              ].map((field, index) => (
                <div key={index}>
                  <label
                    className={`block text-sm font-semibold mb-3 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    required
                    className={`w-full px-6 py-4 rounded-xl transition-all duration-300 focus:scale-105 ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                        : "bg-gray-50 border-gray-300 focus:border-blue-600"
                    } border-2 focus:outline-none`}
                    placeholder={field.placeholder}
                  />
                </div>
              ))}

              <div>
                <label
                  className={`block text-sm font-semibold mb-3 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className={`w-full px-6 py-4 rounded-xl transition-all duration-300 focus:scale-105 resize-none ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                      : "bg-gray-50 border-gray-300 focus:border-blue-600"
                  } border-2 focus:outline-none`}
                  placeholder="Tell me about your project..."
                ></textarea>
              </div>

              {/* Success Message */}
              {showSuccess && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-xl">
                  <CheckCircle className="w-5 h-5" />
                  <span>
                    Form submitted! Your email client should open now.
                  </span>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-xl hover:shadow-2xl hover:shadow-blue-500/25 transform hover:-translate-y-2 transition-all duration-300 font-bold text-lg flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Send Message
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
